/**
 * BrandLockup — shared presentational brand lockup (logo + vertical divider +
 * subtitle). Used by both the authenticated AppHeader and the public Header so
 * the brand markup lives in one place.
 *
 * Presentational ONLY: this component must NOT import or call any MSAL / auth
 * hooks (useMsal, useUserPhoto, useCurrentUser, ...). Keeping it auth-free is
 * what lets the public (unauthenticated) header reuse it.
 *
 * Props:
 *   - to?: string            when set, the lockup becomes a react-router <Link>;
 *                            otherwise a plain <div> (the public header is not a
 *                            link, so it omits `to`).
 *   - subtitle: string       the full subtitle text.
 *   - shortSubtitle?: string when set, renders the responsive full→short swap at
 *                            the 768px breakpoint (AppHeader behavior).
 */

import { Link } from 'react-router-dom';
import { makeStyles, tokens } from '@fluentui/react-components';
import uhealthLogo from '@/assets/uhealth-logo.svg';

const useStyles = makeStyles({
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    textDecoration: 'none',
    color: 'inherit',
  },
  // Crop the SVG lockup to just the U-mark on small screens.
  logo: {
    height: '38px',
    width: 'auto',
    '@media (max-width: 640px)': {
      width: '43px',
      objectFit: 'cover',
      objectPosition: 'left',
    },
  },
  brandDivider: {
    width: '1px',
    height: '32px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  subtitle: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'nowrap',
  },
  subtitleFull: {
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  subtitleShort: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'inline',
    },
  },
});

interface BrandLockupProps {
  to?: string;
  subtitle: string;
  shortSubtitle?: string;
}

export function BrandLockup({ to, subtitle, shortSubtitle }: BrandLockupProps) {
  const styles = useStyles();

  const inner = (
    <>
      <img
        src={uhealthLogo}
        alt="University of Utah Health"
        className={styles.logo}
      />
      <span className={styles.brandDivider} aria-hidden="true" />
      <span className={styles.subtitle}>
        {shortSubtitle ? (
          <>
            <span className={styles.subtitleFull}>{subtitle}</span>
            <span className={styles.subtitleShort}>{shortSubtitle}</span>
          </>
        ) : (
          subtitle
        )}
      </span>
    </>
  );

  return to ? (
    <Link to={to} className={styles.brand}>
      {inner}
    </Link>
  ) : (
    <div className={styles.brand}>{inner}</div>
  );
}

export default BrandLockup;
